import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { FormProvider } from "react-hook-form"

import { PurchaseCostFormStep } from "./purchaseCostFormStep"
import { PurchaseCostStep1Form } from "./PurchaseCostStep1Form"
import { PurchaseCostStep2Form } from "./PurchaseCostStep2Form"
import { usePurchaseCostForm } from "./usePurchaseCostForm"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { PurchasingCostListEntry } from "@/graphql/codegen/graphql"

interface PurchaseCostFormDialogProps {
  productId: number
  defaultWarehouseAreaId: string | null
  editingCost: PurchasingCostListEntry | null
  onClose: () => void
  onSuccess?: () => void
}

export const PurchaseCostFormDialog: FC<PurchaseCostFormDialogProps> = ({
  productId,
  defaultWarehouseAreaId,
  editingCost,
  onClose,
  onSuccess,
}) => {
  const { methods, step, handleStep1Continue, handleBack, handleSubmit, canSubmit, isEdit } = usePurchaseCostForm({
    productId,
    defaultWarehouseAreaId,
    editingCost,
    onClose,
    onSuccess,
  })

  const isDetailsStep = step === PurchaseCostFormStep.Details

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid='product.purchase-costs.form-dialog'>
      <DialogHeader title={isEdit ? "Edit Pricing" : "Add Pricing"} onClose={onClose} />
      <DialogContent className='px-6 pt-6'>
        <FormProvider {...methods}>
          {isDetailsStep ? <PurchaseCostStep1Form isWarehouseLocked={isEdit} /> : <PurchaseCostStep2Form />}
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-6 pb-6 pt-4'>
        <Button
          appearance='outlined'
          onClick={isDetailsStep ? onClose : handleBack}
          data-testid={
            isDetailsStep
              ? "product.purchase-costs.form-dialog.cancel-button"
              : "product.purchase-costs.form-dialog.back-button"
          }
        >
          {isDetailsStep ? "Cancel" : "Back"}
        </Button>
        {isDetailsStep ? (
          <Button
            appearance='contained'
            onClick={handleStep1Continue}
            data-testid='product.purchase-costs.form-dialog.continue-button'
          >
            Continue
          </Button>
        ) : (
          <Button
            appearance='contained'
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid='product.purchase-costs.form-dialog.save-button'
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
